import React, { useState, useEffect } from 'react';
import type { BannerSlideComponent } from '../../../types/builder';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface BannerSlideRendererProps {
  component: BannerSlideComponent;
}

export const BannerSlideRenderer: React.FC<BannerSlideRendererProps> = ({ component }) => {
  const { settings } = component;
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    if (!settings.autoPlay || settings.slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % settings.slides.length);
    }, settings.autoPlaySpeed);

    return () => clearInterval(interval);
  }, [settings.autoPlay, settings.autoPlaySpeed, settings.slides.length]);

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + settings.slides.length) % settings.slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % settings.slides.length);
  };

  return (
    <div
      style={{
        padding: `${settings.padding}px`,
        position: 'relative',
      }}
    >
      <div
        className="relative overflow-hidden rounded-lg"
        style={{ height: `${settings.height}px` }}
      >
        {settings.slides.map((slide, index) => (
          <div
            key={slide.id}
            className="absolute inset-0 transition-opacity duration-500"
            style={{
              opacity: currentSlide === index ? 1 : 0,
              pointerEvents: currentSlide === index ? 'auto' : 'none',
            }}
          >
            <a
              href={slide.link}
              className="block w-full h-full"
              onClick={(e) => e.preventDefault()}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              {slide.title && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                  <h3 className="text-white text-2xl font-bold">{slide.title}</h3>
                </div>
              )}
            </a>
          </div>
        ))}

        {settings.showArrows && settings.slides.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {settings.showDots && settings.slides.length > 1 && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
            {settings.slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  currentSlide === index
                    ? 'bg-white w-8'
                    : 'bg-white/50 hover:bg-white/75'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

